import { useEffect, useSyncExternalStore } from "react";
import { apiGet } from "@/shared/api";

const POST_POLLS_CAPABILITY = "post-polls-v1";

export type PollCapabilityStatus =
  | "checking"
  | "available"
  | "unavailable"
  | "failed";

type CapabilitiesResponse = {
  capabilities: string[];
};

let cachedStatus: PollCapabilityStatus = "checking";
let preflight: Promise<PollCapabilityStatus> | null = null;
const listeners = new Set<() => void>();

function getSnapshot(): PollCapabilityStatus {
  return cachedStatus;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

function updateStatus(status: PollCapabilityStatus): void {
  if (cachedStatus === status) return;
  cachedStatus = status;
  for (const listener of listeners) listener();
}

function hasPollCapability(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const capabilities = (value as Partial<CapabilitiesResponse>).capabilities;
  return (
    Array.isArray(capabilities) && capabilities.includes(POST_POLLS_CAPABILITY)
  );
}

export function preflightPollCapability(): Promise<PollCapabilityStatus> {
  if (cachedStatus !== "checking") return Promise.resolve(cachedStatus);
  if (preflight) return preflight;
  preflight = apiGet<unknown>("/capabilities", { cache: "no-store" })
    .then((response) => {
      const status = hasPollCapability(response) ? "available" : "unavailable";
      updateStatus(status);
      return status;
    })
    .catch(() => {
      updateStatus("failed");
      return "failed";
    });
  return preflight;
}

export function usePollCapability(): PollCapabilityStatus {
  const status = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => {
    void preflightPollCapability();
  }, []);
  return status;
}

export function resetPollCapabilityForTest(): void {
  preflight = null;
  updateStatus("checking");
}
